import { Search } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PROVIDERS } from '../data/providers';
import type { Provider } from '../types';

const typeFilters = ['All', 'Email', 'Push', 'Sms'] as const;

const typeColor: Record<string, string> = {
	Email: 'text-blue-600 bg-blue-50',
	Push: 'text-purple-600 bg-purple-50',
	Sms: 'text-green-700 bg-green-50',
};

interface ProviderSelectorProps {
	open: boolean;
	onSelect: (provider: Provider) => void;
	onClose: () => void;
}

export function ProviderSelector({
	open,
	onSelect,
	onClose,
}: ProviderSelectorProps) {
	const [filter, setFilter] = useState('All');
	const [search, setSearch] = useState('');

	const filtered = PROVIDERS.filter((p) => {
		const matchFilter = filter === 'All' || p.type === filter;
		const matchSearch =
			search === '' || p.name.toLowerCase().includes(search.toLowerCase());
		return matchFilter && matchSearch;
	});

	const groups = ['Email', 'Push', 'Sms'] as const;

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Select Provider</DialogTitle>
				</DialogHeader>

				<div className="flex items-center gap-3">
					<div className="relative flex-1">
						<Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Search providers..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="h-9 pl-8"
						/>
					</div>
					<div className="flex gap-1 rounded-lg bg-muted p-0.5">
						{typeFilters.map((t) => (
							<button
								type="button"
								key={t}
								onClick={() => setFilter(t)}
								className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
									filter === t
										? 'bg-background text-foreground shadow-sm'
										: 'text-muted-foreground hover:text-foreground'
								}`}
							>
								{t}
							</button>
						))}
					</div>
				</div>

				<div className="space-y-6">
					{groups.map((group) => {
						const items = filtered.filter((p) => p.type === group);
						if (items.length === 0) return null;

						return (
							<div key={group}>
								<h3 className="mb-2 text-xs font-medium uppercase text-muted-foreground">
									{group === 'Sms' ? 'SMS' : group} Providers
								</h3>
								<div className="space-y-2">
									{items.map((p) => (
										<button
											type="button"
											key={p.key}
											disabled={p.disabled}
											onClick={() => !p.disabled && onSelect(p)}
											className={`w-full rounded-lg border p-3 text-left transition-colors hover:border-primary hover:bg-primary/5 ${
												p.disabled
													? 'cursor-default opacity-60'
													: 'cursor-pointer'
											}`}
										>
											<div className="flex items-center gap-2">
												<span className="flex h-6 w-6 items-center justify-center rounded bg-muted text-[10px] font-bold">
													{p.logo}
												</span>
												<span className="font-medium">{p.name}</span>
												<Badge
													variant="outline"
													className={`text-[10px] ${typeColor[p.type] || ''}`}
												>
													{p.type}
												</Badge>
											</div>
											<p className="mt-1 text-xs text-muted-foreground">
												{p.desc}
											</p>
											{p.disabled && (
												<p className="mt-1 text-xs text-muted-foreground">
													⚠ Not accepting new signups
												</p>
											)}
										</button>
									))}
								</div>
							</div>
						);
					})}
				</div>
			</DialogContent>
		</Dialog>
	);
}
